import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  GraduationCap,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  FileText,
  Users,
  Layers,
  FileCheck,
  Shield,
  ChevronDown,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Breadcrumbs from '@/components/Breadcrumbs';
import SEO from '@/components/SEO';
import ROIResultsCapture from '@/components/ROIResultsCapture';
import { Link } from 'react-router-dom';

const Education = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [monthlyInvoices, setMonthlyInvoices] = useState(1000);
  const [costPerInvoice, setCostPerInvoice] = useState(12);
  const [exceptionRate, setExceptionRate] = useState(35);
  const [staffRate, setStaffRate] = useState(22);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentProcessingCost = monthlyInvoices * costPerInvoice;
  const hoursSpentPerMonth = monthlyInvoices * 0.545;
  const exceptionsHandledManually = monthlyInvoices * (exceptionRate / 100);
  const processingTimeReduction = 0.75;
  const hoursSaved = hoursSpentPerMonth * processingTimeReduction;
  const exceptionsHandledByAI = exceptionsHandledManually * 0.9;
  const exceptionResolutionSavings = (exceptionsHandledByAI * 15) / 60 * staffRate;
  const laborSavings = hoursSaved * staffRate;
  const totalMonthlySavings = laborSavings + exceptionResolutionSavings;
  const ezCloudCost = 2500;
  const netMonthlySavings = totalMonthlySavings - ezCloudCost;
  const annualSavings = netMonthlySavings * 12;
  const roi = (annualSavings / (ezCloudCost * 12)) * 100;
  const paybackMonths = ezCloudCost / netMonthlySavings;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [] as Array<{
      '@type': 'Question';
      name: string;
      acceptedAnswer: { '@type': 'Answer'; text: string };
    }>,
  };

  const painPoints = [
    {
      icon: <TrendingUp className="w-6 h-6 text-accent" />,
      title: "Budget cycles don't follow the calendar year",
      body: "AP volume peaks in August (new school year start) and June (year-end close). Most generic AP tools throttle under that peak or require manual coding at exactly the wrong moment. EZ Cloud is built to absorb peak volume through machine-learned coding on known suppliers — the August rush doesn't need more AP staff.",
    },
    {
      icon: <Layers className="w-6 h-6 text-accent" />,
      title: 'Funding sources all need to be tracked separately',
      body: 'Local tax revenue, state allocations, Title I/II/III federal funds, grant funds — each has its own tracking and reporting requirements. Generic AP automation treats a GL account as a GL account. EZ Cloud tracks funding source at the invoice-line level and carries it through to posting.',
    },
    {
      icon: <Users className="w-6 h-6 text-accent" />,
      title: 'Principals approve school-level invoices',
      body: "K-12 districts route school-level spend to principals, not to central-office approvers. Generic tools struggle with this — you can't license a Yardi/Oracle seat for every principal. EZ Cloud approvers don't need an ERP license; they approve in EZ Cloud's web UI or via email.",
    },
    {
      icon: <FileCheck className="w-6 h-6 text-accent" />,
      title: 'Purchase orders dominate, and 3-way match is non-negotiable',
      body: 'Most K-12 districts require 3-way match on POs above a threshold. EZ Cloud handles 3-way match natively — GRN-to-invoice-to-PO matching is standard, not a premium feature.',
    },
    {
      icon: <Shield className="w-6 h-6 text-accent" />,
      title: 'Grant tracking demands audit-trail specificity',
      body: "Federal grant auditors want to trace a dollar from grant award through to vendor payment, including who approved each step. EZ Cloud's audit log records every action, timestamped and user-identified; grant-funded invoices carry the grant ID through to ERP posting.",
    },
    {
      icon: <FileText className="w-6 h-6 text-accent" />,
      title: 'Higher education adds sponsored research and clinical lines',
      body: 'Universities layer sponsored research accounts and, in academic medical centers, clinical lines on top of standard fund accounting. Same machinery — per-supplier defaults, per-fund overrides, audit logging — applied at more dimensions.',
    },
  ];

  const howItWorks = [
    { title: 'Peak-volume throughput', body: 'Machine-learned coding on known suppliers absorbs August and June spikes without headcount.' },
    { title: 'Funding source tracked at the line level', body: 'Title funds, grants, local, state — each tracked and reported separately.' },
    { title: 'Principal-level approvals without ERP licenses', body: "School-level approvers work in EZ Cloud's web UI. Mobile-friendly." },
    { title: '3-way match native', body: 'GRN-to-invoice-to-PO matching standard.' },
    { title: 'Audit trail at the invoice, action, and user level', body: 'Grant auditors can trace a dollar from award to payment.' },
    { title: 'Oracle EBS, Tyler Munis, Skyward, Banner, Workday integrations', body: 'Native integrations with major education ERPs.' },
  ];

  const faqs = [
    {
      question: 'What is AP automation for K-12 school districts?',
      answer:
        'AP automation for K-12 school districts replaces manual invoice capture, coding, and approval with software that handles fund accounting, encumbrance tracking, Title I/II/III federal grant compliance, decentralized principal-level approvals, and 3-way purchase order matching natively. EZ Cloud is designed specifically for K-12 and higher education, integrating with Oracle E-Business Suite, Oracle Fusion Cloud, Tyler Munis, Skyward, Banner, and Workday to maintain the ERP as system of record.',
    },
    {
      question: 'How does AP automation handle fund accounting and encumbrance tracking for schools?',
      answer:
        'Funding source — local tax revenue, state allocations, Title I/II/III federal funds, grant funds — is tracked at the invoice-line level. Encumbrances are checked against PO commitments before invoices advance through approval, ensuring spend does not exceed remaining grant or fund balance. The funding code flows through to ERP posting and is preserved in the audit log for federal grant auditors.',
    },
    {
      question: 'How does AP automation for K-12 differ from corporate AP automation?',
      answer:
        'K-12 districts have requirements corporate AP tools do not handle: fund accounting at the line level, federal grant audit trails, principal-level approval routing without ERP licenses, peak volume in August and June tied to the school-year calendar, and 3-way match as a non-negotiable on most POs. EZ Cloud is built for these patterns rather than generalized for corporate workflows.',
    },
    {
      question: 'Which education ERPs does EZ Cloud integrate with?',
      answer:
        'Oracle E-Business Suite, Oracle Fusion Cloud, Tyler Munis, Skyward, Banner (Ellucian), Workday Student and Finance, and PeopleSoft. Integrations are native — direct API write-back, not middleware.',
    },
    {
      question: 'How does EZ Cloud handle federal grant tracking?',
      answer:
        'Grant identifiers attach to the invoice or invoice line at coding. The grant ID flows through to ERP posting and is preserved in the audit log. Grant-funded spend can be reported separately without leaving EZ Cloud.',
    },
    {
      question: 'Can principals approve school-level invoices without an ERP license?',
      answer:
        'Yes. Approvers work entirely in EZ Cloud. Approvals post into the ERP via EZ Cloud\'s integration after sign-off. Principals do not need an Oracle, Tyler, or Skyward seat.',
    },
    {
      question: 'How does 3-way match work for school district POs?',
      answer:
        'Invoices are matched against the PO and the goods-receipt note (GRN). Quantity, price, and line variance thresholds are configurable. Exceptions route to the requisitioner or the central AP team; clean matches auto-approve up to the configured amount threshold.',
    },
    {
      question: 'What about school-year peak volume?',
      answer:
        'Machine-learned coding on known suppliers means the August and June peaks don\'t require additional human coding. The AP team reviews exceptions, not routine invoices.',
    },
    {
      question: 'How are student activity funds handled?',
      answer:
        'Student activity fund invoices carry the activity fund code at the line level and post into the ERP\'s activity-fund accounting module. Approval routes through the activity sponsor plus school principal.',
    },
    {
      question: "What's the audit posture for federal grant spend?",
      answer:
        'Every invoice action is timestamped and user-identified. Grant IDs, approvals, match variances, and payment dates are all queryable and exportable. Federal grant auditors can trace a dollar from award to vendor payment without leaving the audit export.',
    },
    {
      question: "What's implementation like for a K-12 district?",
      answer:
        'Typical timeline is 10–12 weeks. Phases: ERP integration setup (2–3 weeks), supplier and chart-of-accounts migration (2 weeks), approval workflow configuration including principal roles (2 weeks), training (2 weeks), parallel run and cut-over (2 weeks).',
    },
    {
      question: 'Does EZ Cloud handle requisition-to-pay, or just pay?',
      answer:
        "EZ Cloud handles invoice receipt through payment. Requisitions live in the ERP. EZ Cloud matches invoices to the PO produced by the ERP's requisition module.",
    },
    {
      question: "What's the security posture?",
      answer:
        'SOC 2 Type II certified. Encryption in transit and at rest. Role-based access. Audit logging. SSO via SAML 2.0 and OIDC.',
    },
  ];

  faqSchema.mainEntity = faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  }));

  return (
    <div className="min-h-screen">
      <SEO
        title="AP Automation for Schools, Districts & Universities | EZ Cloud"
        description="AP automation built for the education sector's realities: school-year budget cycles, fund accounting, grant tracking, and principal-level approval patterns."
        canonical="https://ezcloud.ai/industries/education"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Industries', url: 'https://ezcloud.ai/#industries' },
          { name: 'Education', url: 'https://ezcloud.ai/industries/education' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* Oracle EBS solution cross-link */}
      <div className="bg-accent/10 border-b border-accent/20">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-700">
          <span className="font-medium">Running Oracle E-Business Suite?</span>
          <Link
            to="/solutions/education-oracle-ebs"
            className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
          >
            See the Education-on-Oracle-EBS solution
            <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
          </Link>
        </div>
      </div>

      {/* Oracle WebCenter modernization cross-link */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-700">
          <span className="font-medium">Running Oracle WebCenter AP today?</span>
          <Link
            to="/solutions/modernize-oracle-webcenter"
            className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
          >
            See the Oracle WebCenter AP modernization path
            <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="bg-hero-gradient text-white min-h-[80vh] relative overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto px-4 relative py-20">
          <div className="max-w-4xl space-y-8">
            <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2 text-sm">
              <GraduationCap className="w-4 h-4 mr-2" />
              Built for Education
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
              Accounts payable automation for
              <span className="text-accent"> schools, districts, and universities</span>
            </h1>

            <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl">
              AP automation built for the education sector's realities: school-year budget cycles, fund accounting, grant tracking, and the principal-level approval patterns that generic tools can't model.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book an education AP demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="font-semibold px-8 py-6 text-lg"
                asChild
              >
                <Link to="/integrations">See supported ERPs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              What's different about education AP
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {painPoints.map((p, i) => (
              <Card key={i} className="border border-gray-200 bg-white h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      {p.icon}
                    </div>
                    <CardTitle className="text-xl text-gray-900 leading-snug">
                      {i + 1}. {p.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                How it works for education
              </h2>
            </div>

            <div className="space-y-4">
              {howItWorks.map((step, i) => (
                <div key={i} className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-700">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold mb-4">Estimate your savings</h2>
              <p className="text-lg text-white/90">
                Model the impact of EZ Cloud against your district or university's current AP costs.
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Monthly invoice volume:{' '}
                        <span className="text-primary">{monthlyInvoices.toLocaleString()}</span>
                      </label>
                      <input
                        type="range"
                        min="200"
                        max="5000"
                        step="100"
                        value={monthlyInvoices}
                        onChange={(e) => setMonthlyInvoices(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Current cost per invoice: <span className="text-primary">${costPerInvoice}</span>
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="25"
                        step="1"
                        value={costPerInvoice}
                        onChange={(e) => setCostPerInvoice(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Exception rate: <span className="text-primary">{exceptionRate}%</span>
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="50"
                        step="5"
                        value={exceptionRate}
                        onChange={(e) => setExceptionRate(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Average staff hourly rate: <span className="text-primary">${staffRate}</span>
                      </label>
                      <input
                        type="range"
                        min="15"
                        max="40"
                        step="1"
                        value={staffRate}
                        onChange={(e) => setStaffRate(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Net monthly savings</div>
                      <div className="text-4xl font-bold text-primary">
                        ${netMonthlySavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="border-t border-gray-300 pt-4">
                      <div className="text-sm text-gray-600 mb-1">Annual savings</div>
                      <div className="text-3xl font-bold text-accent">
                        ${annualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 border-t border-gray-300 pt-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">ROI</div>
                        <div className="text-2xl font-bold text-green-600">{roi.toFixed(0)}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Payback</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {paybackMonths.toFixed(1)} mo
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 pt-2 border-t border-gray-300">
                      Estimates. Model uses 88–92% touchless processing and up to 90% email-exception prevention as a baseline. Individual results vary by ERP, invoice mix, and process maturity.
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <ROIResultsCapture
                    industry="Education"
                    annualSavings={annualSavings}
                    inputs={{
                      'Monthly invoices': monthlyInvoices.toLocaleString(),
                      'Avg cost per invoice': `$${costPerInvoice}`,
                      'Exception rate': `${exceptionRate}%`,
                      'Staff hourly rate': `$${staffRate}`,
                    }}
                    buttonLabel="Email my education ROI report"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud resources for education AP
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                How K-12 and higher-ed AP automation connects to your ERP, related verticals, and the underlying technology.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/integrations/oracle"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Oracle EBS & Fusion
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native AP integration with Oracle EBS and Fusion Cloud ERP — the most common ERP across large K-12 districts and universities.
                </p>
              </Link>

              <Link
                to="/integrations/netsuite"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  NetSuite
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  AP automation for smaller districts and private institutions running NetSuite — OneWorld subsidiary routing, fund-aware coding.
                </p>
              </Link>

              <Link
                to="/integrations/dynamics"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Microsoft Dynamics 365
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  F&O and Business Central integration for universities and larger districts on Dynamics 365 — multi-entity, fund-aware coding.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers — processing time reductions, grant audit-readiness wins, principal-level approval at scale.
                </p>
              </Link>

              <Link
                to="/resources/roi-calculator"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Full ROI calculator
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Detailed ROI modeling across all industries, with adjustable assumptions and industry shortcuts.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  How native ERP integration works
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time APIs, audit-trail integrity, SOC 2 compliance — the architecture behind every EZ Cloud education deployment.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <Card
                  key={index}
                  className="border border-gray-200 bg-white cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <CardTitle className="text-lg text-left text-gray-900 font-semibold">
                        {faq.question}
                      </CardTitle>
                      <ChevronDown
                        className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </CardHeader>
                  {openFaq === index && (
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Ready to see EZ Cloud on your district or university ERP?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Book a 30-minute discovery call and we'll walk through the integration against your setup.
            </p>
            <div className="flex justify-center">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book an education AP demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Education;
